import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useWalletBalances } from '../services/walletBalanceService';

interface ConnectWalletProps {
  isOpen: boolean;
  onClose: () => void;
  onWalletConnected: (address: string, provider: 'phantom' | 'solflare') => Promise<void>;
}

// Logo URLs
const LOGOS = {
  EKKO: "https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png",
  PHANTOM: "https://187760183-files.gitbook.io/~/files/v0/b/gitbook-x-prod.appspot.com/o/spaces%2F-MVOiF6Zqit57q_hxJYp%2Ficon%2FU7kNZ4ygz4QW1rUwOuTT%2FWhite%20Ghost_docs_nu.svg?alt=media&token=447b91f6-db6d-4791-902d-35d75c19c3d1",
  SOLFLARE: "https://www.solflare.com/wp-content/uploads/2024/11/App-Icon.svg"
};

const ConnectWallet: React.FC<ConnectWalletProps> = ({ isOpen, onClose, onWalletConnected }) => {
  const [phantomInstalled, setPhantomInstalled] = useState(false);
  const [solflareInstalled, setSolflareInstalled] = useState(false);
  const { refreshBalances } = useWalletBalances();

  useEffect(() => {
    // Check for wallet extensions
    setPhantomInstalled(!!window.solana?.isPhantom);
    setSolflareInstalled(!!window.solflare);
  }, [isOpen]);

  if (!isOpen) return null;

  const connectPhantom = async () => {
    try {
      if (window.solana) {
        await window.solana.connect();
        
        // Immediately fetch token balances after connecting
        if (window.solana.publicKey) {
          const address = window.solana.publicKey.toString();
          console.log('Connected to Phantom wallet:', address);
          
          // Fetch token balances
          await refreshBalances(address);
          
          // Notify parent component about wallet connection
          await onWalletConnected(address, 'phantom');
        }
        
        onClose();
      }
    } catch (error) {
      console.error("Error connecting to Phantom:", error);
    }
  };

  const connectSolflare = async () => {
    try {
      if (window.solflare) {
        await window.solflare.connect();
        
        // Immediately fetch token balances after connecting
        if (window.solflare.publicKey) {
          const address = window.solflare.publicKey.toString();
          console.log('Connected to Solflare wallet:', address);
          
          // Fetch token balances
          await refreshBalances(address);
          
          // Notify parent component about wallet connection
          await onWalletConnected(address, 'solflare');
        }
        
        onClose();
      }
    } catch (error) {
      console.error("Error connecting to Solflare:", error);
    }
  };

  const getPhantom = () => {
    window.open('https://phantom.app/', '_blank');
  };

  const getSolflare = () => {
    window.open('https://solflare.com/', '_blank');
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center z-50 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300`}>
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      
      {/* Modal container */}
      <div className="relative w-full max-w-md rounded-xl shadow-xl border border-[#24c35b]/20 glassmorphism overflow-hidden">
        {/* Green accent border at top */}
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#24c35b] to-transparent opacity-50"></div>
        
        <div className="p-6">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <X size={16} />
          </button>
          
          {/* Modal content */}
          <div className="mb-6 text-center">
            <img 
              src={LOGOS.EKKO}
              alt="EKKO Logo"
              className="h-8 mb-3 mx-auto"
            />
            <h3 className="text-xl font-semibold mb-1 text-gray-900">Connect Your Wallet</h3>
            <p className="text-gray-600 text-sm">
              Connect your wallet to access the EKKO platform.
            </p>
          </div>
          
          {/* Wallet options */}
          <div className="space-y-2">
            {/* Phantom */}
            <div 
              onClick={phantomInstalled ? connectPhantom : getPhantom}
              className="glassmorphism-gray border border-[#24c35b]/20 rounded-lg mb-1 overflow-hidden transition-all cursor-pointer hover:border-[#24c35b]/30 group relative"
            >
              {/* Card content */}
              <div className="p-4 relative z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#24c35b]/10 rounded-lg">
                      <img 
                        src={LOGOS.PHANTOM}
                        alt="Phantom"
                        className="w-5 h-5"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 group-hover:text-[#24c35b] transition-colors">Phantom</h3>
                      <p className="text-xs text-gray-600">Popular choice for Solana</p>
                    </div>
                  </div>
                  
                  <div className={`px-3 py-1.5 text-xs font-medium rounded-md ${phantomInstalled 
                    ? 'bg-[#24c35b]/10 text-[#24c35b] border border-[#24c35b]/30 group-hover:bg-[#24c35b]/20' 
                    : 'bg-[#24c35b]/5 text-gray-600 border border-gray-300/50 group-hover:text-[#24c35b] group-hover:border-[#24c35b]/20'}`}
                  >
                    {phantomInstalled ? 'Connect' : 'Install'}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Solflare */}
            <div 
              onClick={solflareInstalled ? connectSolflare : getSolflare}
              className="glassmorphism-gray border border-[#24c35b]/20 rounded-lg overflow-hidden transition-all cursor-pointer hover:border-[#24c35b]/30 group relative"
            >
              {/* Card content */}
              <div className="p-4 relative z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#24c35b]/10 rounded-lg">
                      <img 
                        src={LOGOS.SOLFLARE}
                        alt="Solflare"
                        className="w-5 h-5"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 group-hover:text-[#24c35b] transition-colors">Solflare</h3>
                      <p className="text-xs text-gray-600">Feature-rich Solana wallet</p>
                    </div>
                  </div>
                  
                  <div className={`px-3 py-1.5 text-xs font-medium rounded-md ${solflareInstalled 
                    ? 'bg-[#24c35b]/10 text-[#24c35b] border border-[#24c35b]/30 group-hover:bg-[#24c35b]/20' 
                    : 'bg-[#24c35b]/5 text-gray-600 border border-gray-300/50 group-hover:text-[#24c35b] group-hover:border-[#24c35b]/20'}`}
                  >
                    {solflareInstalled ? 'Connect' : 'Install'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="mt-6 pt-3 border-t border-[#24c35b]/10 text-xs text-gray-600 flex justify-between">
            <p>Don't have a wallet?</p>
            <a 
              href="https://solana.com/ecosystem/wallets" 
              target="_blank"
              rel="noreferrer"
              className="text-[#24c35b] hover:underline"
            >
              Learn more
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectWallet;